// src/hooks/useFaxNotices.js
// Fax pop-ups on every page (3.24.0). Polls the backend's /portal/fax-events
// feed, which returns only fax results this person can see in Outbox / Inbox
// (same rules as the lists): their own outgoing faxes for Users, their teams'
// for Managers, the whole client for Admins, everything for Super Admins
// (minus clients they muted in Settings). Outgoing and incoming are separate
// event kinds with separate wording.
//
// Pop-ups follow the "Pop-up notices for fax updates" switch in Settings.
// Polling pauses while the window is hidden and catches up (up to 10
// minutes back) when it's shown again. Several open portal tabs share the
// list of shown events, so each event pops up once per browser.
import { useEffect, useRef } from 'react';
import { fetchFaxEvents, fetchFailedAgain } from '../utils/api';
import { formatFax, formatRetryWindow, formatRetryWindowDay } from '../utils/format';
import { reasonText, reasonWhy, incomingReasonText } from '../utils/faxReasons';

export const FAX_EVENTS_POLL_MS = 20 * 1000;
// 3.66.1 (owner, Oct 23: the Overview's In progress still said sending after
// the delivered email came): every 5 s while a fax is going out, after Send
// in Compose (10 minutes) or while the Overview shows one sending, waiting
// or arriving; otherwise every 20 s as before
export const FAX_EVENTS_FAST_MS = 5 * 1000;
let fastUntil = 0;
const fastHolds = new Set();
export function watchFaxesFast(ms = 10 * 60000) { fastUntil = Math.max(fastUntil, Date.now() + ms); }
export function holdFaxesFast(key, on) { if (on) fastHolds.add(key); else fastHolds.delete(key); }
const fastNow = () => fastHolds.size > 0 || Date.now() < fastUntil;
const OVERLAP_MS  = 60 * 1000;          // re-ask a little further back; shown events are skipped
const SHOWN_KEY   = 'faxPortal.shownFaxEvents';
const SHOWN_TTL   = 30 * 60 * 1000;
const MAX_SINGLE  = 4;                  // more events than this at once → one summary pop-up

function readShown() {
  try {
    const now = Date.now();
    const raw = JSON.parse(window.localStorage.getItem(SHOWN_KEY) || '{}');
    return Object.fromEntries(Object.entries(raw).filter(([, t]) => now - t < SHOWN_TTL));
  } catch { return {}; }
}
function markShown(keys) {
  try {
    const shown = readShown();
    keys.forEach(k => { shown[k] = Date.now(); });
    window.localStorage.setItem(SHOWN_KEY, JSON.stringify(shown));
  } catch { /* storage off: pop-ups may repeat across tabs, nothing worse */ }
}

// 3.57.7: "failed again" windows already shown (kept 2 hours, longer than the
// hour they cover, so coming back into the portal doesn't show one twice)
const AGAIN_KEY = 'faxPortal.shownFailedAgain', AGAIN_TTL = 2 * 3600 * 1000;
function againShown(key) {
  try { const t = JSON.parse(window.localStorage.getItem(AGAIN_KEY) || '{}')[key]; return !!t && Date.now() - t < AGAIN_TTL; } catch { return false; }
}
function markAgain(key) {
  try {
    const now = Date.now();
    const all = Object.fromEntries(Object.entries(JSON.parse(window.localStorage.getItem(AGAIN_KEY) || '{}')).filter(([, t]) => now - t < AGAIN_TTL));
    all[key] = now;
    window.localStorage.setItem(AGAIN_KEY, JSON.stringify(all));
  } catch { /* storage off: it may show again on another tab */ }
}

function pagesText(n) {
  return n ? `${n} page${n === 1 ? '' : 's'}` : null;
}
// One event → the pop-up's tone, title and detail
export function noticeFor(ev, { showClient = false } = {}) {
  const num    = formatFax(ev.number);
  const client = showClient && ev.accountName ? `${ev.accountName} · ` : '';
  switch (ev.kind) {
    // Outgoing
    case 'delivered':
      return { tone: 'success', icon: '✅', title: `${client}Fax delivered to ${num}`, detail: ev.fileName || pagesText(ev.pages) };
    // 3.57.7 (owner): the reason in plain words and why / what to do, as in the emails
    case 'failed':
      // 3.78.0 (owner, approved Nov 3): a High failure congestion number: red,
      // and why the retry waits and that there's no need to resend
      if (ev.retryAt && ev.congested) {
        const why = reasonText(ev.failureReason, ev.failureContext);
        return { tone: 'error', icon: '⏳', title: `${client}Fax to ${num} failed: ${why.charAt(0).toLowerCase()}${why.slice(1)}`,
          detail: `This is a high failure congestion fax number: it's busy most of the day. The system has found the times it usually takes faxes, and your fax will be retried automatically then, starting around ${formatRetryWindowDay(ev.retryAt)}. You don't need to resend it. If every try fails, we'll email you so you can send it again.` };
      }
      return ev.retryAt
        ? { tone: 'warning', icon: '⏳', title: `${client}Fax to ${num} failed`, detail: `${reasonText(ev.failureReason, ev.failureContext)}. Retrying automatically around ${formatRetryWindow(ev.retryAt)}.` }
        : { tone: 'error', icon: '❌', title: `${client}Fax to ${num} failed`, detail: `${reasonText(ev.failureReason, ev.failureContext)}. ${reasonWhy(ev.failureReason, ev.failureContext)}`.trim() };
    case 'autoRetrySent':
      return { tone: 'info', icon: '🔁', title: `${client}Automatic retry sent to ${num}`, detail: ev.attempt ? `Attempt ${ev.attempt}` : ev.fileName };
    // Incoming
    case 'received':
      return { tone: 'success', icon: '📥', title: `${client}New fax received from ${num}`, detail: pagesText(ev.pages) || '' };
    case 'incomingFailed':
      return { tone: 'error', icon: '📥', title: `${client}Received fax from ${num} failed`, detail: ev.failureReason ? incomingReasonText(ev.failureReason) : '' };   // 3.62.23: from our side
    default:
      return null;
  }
}

// onActivity (3.26.0): called when any new fax event arrives (also one
// another browser tab already showed), so the Inbox / Outbox counts in the
// menu refresh at once instead of at their next minute. 3.36.5: called with
// the feed's activity ({ outgoing, incoming }: when something last changed
// on each side, including faxes that started or joined a line), so every
// open page updates by itself; the feed runs even with pop-ups off.
// allow(ev) (3.30.1): the person's own pop-up kinds (Preferences)
// popups (3.36.5): show pop-ups (Preferences switch); false = activity only
// onFailedAgain (3.57.7, owner): the person's own send failed again to the
// same number (the 2nd or later failure within an hour): the window that
// stays open. Shown even with pop-ups off, once per failure.
export function useFaxNotices({ enabled, popups = true, getToken, notify, showClient, onOpen, onActivity, allow, onFailedAgain }) {
  const sinceRef  = useRef(null);
  const tokenRef  = useRef(getToken);
  const notifyRef = useRef(notify);
  const openRef   = useRef(onOpen);
  const activityRef = useRef(onActivity);
  activityRef.current = onActivity;
  const allowRef = useRef(allow);
  allowRef.current = allow;
  tokenRef.current  = getToken;
  notifyRef.current = notify;
  openRef.current   = onOpen;
  const popupsRef = useRef(popups);
  popupsRef.current = popups;
  const againRef = useRef(onFailedAgain);
  againRef.current = onFailedAgain;

  useEffect(() => {
    if (!enabled) { sinceRef.current = null; return; }
    let stopped = false, busy = false, pausedUntil = 0, last = 0;

    async function tick() {
      if (busy || stopped || document.hidden || Date.now() < pausedUntil) return;
      busy = true;
      last = Date.now();
      try {
        const since = sinceRef.current
          ? new Date(Date.parse(sinceRef.current) - OVERLAP_MS).toISOString()
          : null;
        const { now, events = [], activity } = await fetchFaxEvents(since, () => tokenRef.current());
        if (stopped) return;
        sinceRef.current = now || new Date().toISOString();
        if (!since) {                             // first call only sets the starting point
          // 3.57.7 (owner): coming into the portal, their own fax that failed
          // again within the last hour (also one sent by email) still opens the window
          if (againRef.current) {
            const ev = await fetchFailedAgain(() => tokenRef.current()).catch(() => null);
            if (ev && !stopped && !againShown(ev.key)) { markAgain(ev.key); againRef.current(ev); }
          }
          return;
        }
        if (events.length || activity?.outgoing || activity?.incoming) activityRef.current?.(activity || {}, events);
        const again = events.filter(ev => ev.kind === 'failed' && ev.mine && ev.repeat >= 2 && ev.key && !againShown(ev.key));
        if (again.length && againRef.current) {
          again.forEach(ev => markAgain(ev.key));
          againRef.current(again[again.length - 1]);
        }
        if (!popupsRef.current) return;

        const shown = readShown();
        const fresh = events.filter(ev => ev.key && !shown[ev.key] && (allowRef.current ? allowRef.current(ev) : true));
        if (!fresh.length) return;
        markShown(fresh.map(ev => ev.key));

        if (fresh.length > MAX_SINGLE) {
          const out = fresh.filter(e => e.direction === 'outgoing').length;
          const inc = fresh.length - out;
          const parts = [out && `${out} sent`, inc && `${inc} received`].filter(Boolean).join(', ');
          notifyRef.current({ tone: 'info', icon: '📠', title: `${fresh.length} fax updates`, detail: parts,
            onClick: () => openRef.current?.(out >= inc ? 'outgoing' : 'incoming', null) });
          return;
        }
        for (const ev of fresh) {
          const n = noticeFor(ev, { showClient });
          if (!n) continue;
          notifyRef.current({ ...n,
            onClick: () => openRef.current?.(ev.direction === 'outgoing' ? 'outgoing' : 'incoming', ev.faxId) });
        }
      } catch (e) {
        // A missed poll just means no pop-ups this round. 3.62.16 (audit M3):
        // "not allowed" (or not signed in yet, e.g. two-factor setup): ask
        // again in 5 minutes instead of every few seconds
        if (e?.status === 401 || e?.status === 403) pausedUntil = Date.now() + 5 * 60000;
      } finally {
        busy = false;
      }
    }

    const timer = setInterval(() => { if (fastNow() || Date.now() - last >= FAX_EVENTS_POLL_MS - 500) tick(); }, FAX_EVENTS_FAST_MS);
    tick();
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stopped = true; clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [enabled, showClient]);
}
